import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const faqs = [
  {
    question: 'What is Pomogrow?',
    answer:
      'Pomogrow is a gamified Pomodoro focus timer. Start a focus session and grow a virtual tomato plant while you work. Stay focused for the full session and your plant thrives. Leave the app early and your plant withers. Over time, you build a garden of tomato plants that represents your productivity.',
  },
  {
    question: 'How do I start a focus session?',
    answer:
      'Open Pomogrow, choose your session duration (default is 25 minutes), select a tomato variety, and tap Start. Stay focused — your tomato plant grows as the timer progresses. When the timer completes, your fully-grown plant is added to your garden.',
  },
  {
    question: 'Do I need to create an account?',
    answer:
      'No. Pomogrow works in guest mode with no sign-up required. All your data is stored locally on your device. You can create an account later if you want to sync your data across devices.',
  },
  {
    question: 'What tomato varieties are available?',
    answer:
      'Free tier includes Roma and Cherry varieties. Premium unlocks all varieties including Beefsteak, Heirloom, San Marzano, Grape, Plum, and Brandywine.',
  },
  {
    question: 'What happens if I leave the app during a session?',
    answer:
      'If you leave the app for more than 30 seconds during a session, your tomato plant will wither and die. This encourages you to stay focused. There is a brief grace period for accidental switches. Make sure Do Not Disturb mode is enabled and screen auto-lock is set longer than your session.',
  },
  {
    question: 'What do I get with Pomogrow Premium?',
    answer:
      'Premium unlocks: unlimited daily focus sessions (free tier is limited to 2 per day), all 8 tomato varieties, calendar view to visualise your focus history, analytics dashboard with detailed productivity insights, and ambient sounds to enhance your sessions.',
  },
  {
    question: 'How much does Premium cost?',
    answer:
      'Web (Stripe): £3.99/month, £9.99/quarter, £19.99/year, or £49.99 lifetime (one-time, web only). iOS (Apple IAP): £3.99/month or £19.99/year. All prices include VAT where applicable.',
  },
  {
    question: 'How do I cancel my subscription?',
    answer:
      'On the web: go to Settings > Premium > Manage Subscription to access the Stripe Customer Portal. On iOS: go to device Settings > Apple ID > Subscriptions > Pomogrow > Cancel Subscription. Your Premium features remain active until the end of your current billing period.',
  },
  {
    question: 'Can I get a refund?',
    answer:
      'For web (Stripe) purchases, contact us at support@wolftechnologies.co.uk within 14 days of purchase for a full refund. For iOS (Apple) purchases, visit reportaproblem.apple.com to request a refund from Apple.',
  },
  {
    question: 'I subscribed on the web. Can I use Premium on iOS?',
    answer:
      'Yes, as long as you are signed in with the same account on both platforms. Your subscription status syncs across devices when you are signed in.',
  },
  {
    question: 'How is my data stored?',
    answer:
      'In guest mode, all data is stored locally on your device using IndexedDB — nothing is sent to our servers. For signed-in users, data is synced to the cloud and stored securely in AWS EU West 2 (London), encrypted in transit and at rest.',
  },
  {
    question: 'How do I delete my account and data?',
    answer:
      'Go to Settings > Account > Delete Account in the app, or email support@wolftechnologies.co.uk with the subject line "Data Deletion Request". All associated data will be permanently deleted within 30 days.',
  },
  {
    question: 'My sessions are not syncing across devices',
    answer:
      'Confirm you are signed in with the same account on all devices. Check your internet connection. Pull down to refresh on the main screen. Try signing out and back in.',
  },
  {
    question: 'I purchased Premium but it is not showing',
    answer:
      'Make sure you are signed in with the account you used to purchase. For iOS, go to Settings > Premium > Restore Purchases. For web, refresh the page or sign out and back in. If the issue persists, contact us with your purchase receipt.',
  },
  {
    question: 'My plant withered even though I did not leave the app',
    answer:
      'There is a 30-second grace period for brief interruptions. If you were away longer than 30 seconds, the plant will wither. Make sure Do Not Disturb mode is enabled and screen auto-lock is set to a longer duration than your session.',
  },
];

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className={`faq__item ${isOpen ? 'faq__item--open' : ''}`} onClick={onClick}>
    <div className="faq__question">
      <h3>{question}</h3>
      <svg
        className="faq__chevron"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </div>
    {isOpen && (
      <div className="faq__answer">
        <p>{answer}</p>
      </div>
    )}
  </div>
);

const PomogrowSupport = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="legal-page">
      <div className="legal-page__header">
        <div className="container">
          <Link to="/" className="legal-page__back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <span className="section__tag">Support</span>
          <h1 className="legal-page__title">Pomogrow Support</h1>
          <p className="legal-page__subtitle">
            Get help with Pomogrow by Wolf Technologies Ltd
          </p>
        </div>
      </div>

      <div className="container">
        <div className="legal-page__content">

          {/* Contact Section */}
          <section className="support-contact">
            <div className="support-contact__card">
              <div className="support-contact__icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <div>
                <h3>Need help?</h3>
                <p>
                  Contact us at{' '}
                  <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>
                </p>
                <p className="support-contact__response">
                  We aim to respond to all support enquiries within 48 hours.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="legal-section">
            <h2>Frequently Asked Questions</h2>
            <div className="faq__list">
              {faqs.map((faq, index) => (
                <FAQItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openIndex === index}
                  onClick={() => toggleFAQ(index)}
                />
              ))}
            </div>
          </section>

          {/* App Info */}
          <section className="legal-section">
            <h2>App Information</h2>
            <div className="support-info__grid">
              <div className="support-info__item">
                <span className="support-info__label">App</span>
                <span className="support-info__value">Pomogrow</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Version</span>
                <span className="support-info__value">1.0</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Developer</span>
                <span className="support-info__value">Wolf Technologies Ltd</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Platform</span>
                <span className="support-info__value">iOS</span>
              </div>
            </div>
          </section>

          {/* Links */}
          <section className="legal-section">
            <h2>Useful Links</h2>
            <div className="support-links">
              <Link to="/pomogrow/privacy" className="support-links__item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                Privacy Policy
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" />
                  <path d="M12 5l7 7-7 7" />
                </svg>
              </Link>
              <a href="/" className="support-links__item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                WolfTechnologies Home
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" />
                  <path d="M12 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PomogrowSupport;
